import classNames from "classnames";
import { motion, useMotionValue, useSpring } from "motion/react";
import { memo, useCallback, useRef } from "react";
import * as styles from "./tilt-card.module.css";

const SPRING = { damping: 30, stiffness: 100, mass: 2 };
const ROTATE_AMPLITUDE = 6;
const SCALE_ON_HOVER = 1.02;

export const TiltCard = memo(function TiltCard({
  className,
  image,
  label,
  subtext,
  active,
  selfServe,
  onClick,
  onImageLoad,
}: {
  className?: string;
  image: string;
  label: string;
  subtext: string;
  active: boolean;
  selfServe?: boolean;
  onClick: () => void;
  onImageLoad?: () => void;
}) {
  const ref = useRef<HTMLElement>(null);

  const rotateX = useSpring(useMotionValue(0), SPRING);
  const rotateY = useSpring(useMotionValue(0), SPRING);
  const scale = useSpring(1, SPRING);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const el = ref.current;
      if (!el) {
        return;
      }
      const rect = el.getBoundingClientRect();

      const offsetX = e.clientX - rect.left - rect.width / 2;
      const offsetY = e.clientY - rect.top - rect.height / 2;

      rotateX.set((offsetY / (rect.height / 2)) * -ROTATE_AMPLITUDE);
      rotateY.set((offsetX / (rect.width / 2)) * ROTATE_AMPLITUDE);
    },
    [rotateX, rotateY],
  );

  const handleMouseEnter = useCallback(() => {
    scale.set(SCALE_ON_HOVER);
  }, [scale]);

  const handleMouseLeave = useCallback(() => {
    scale.set(1);
    rotateX.set(0);
    rotateY.set(0);
  }, [scale, rotateX, rotateY]);

  return (
    <figure
      className={classNames(styles.cardFigure, className)}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      ref={ref}
    >
      <motion.div
        className={styles.cardInner}
        style={{ rotateX, rotateY, scale }}
      >
        <img
          alt=""
          className={styles.cardImage}
          height={200}
          loading="eager"
          onError={onImageLoad}
          onLoad={onImageLoad}
          src={image}
          width={400}
        />

        <span className={styles.cardLabel}>{label}</span>
        <span
          className={classNames(styles.cardPill, {
            [styles.cardPillActive!]: active,
            [styles.cardPillSelfServe!]: !active && selfServe,
          })}
        >
          <span className={styles.cardPillText}>
            {active ? "Active" : subtext}
          </span>
        </span>
      </motion.div>
    </figure>
  );
});
